import { useState, useCallback, useEffect } from 'react';
import { Button } from '../../components/Button';
import type { ProfileUser, ApiResponseWithUser } from '../../types/profile';

interface BillingInfoSectionProps {
    user: ProfileUser;
    onUpdate: (data: Partial<ProfileUser>) => Promise<ApiResponseWithUser>;
    isLoading: boolean;
}

export function BillingInfoSection({ user, onUpdate, isLoading }: BillingInfoSectionProps) {
    const [formData, setFormData] = useState({
        billing_first_name: user.billing_first_name,
        billing_last_name: user.billing_last_name,
        billing_dni: user.billing_dni,
        billing_birth_date: user.billing_birth_date,
    });
    const [error, setError] = useState<string | null>(null);
    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
        setFormData({
            billing_first_name: user.billing_first_name,
            billing_last_name: user.billing_last_name,
            billing_dni: user.billing_dni,
            billing_birth_date: user.billing_birth_date,
        });
        setIsDirty(false);
    }, [user]);

    const handleChange = useCallback((field: keyof typeof formData) => (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        const value = e.target?.value ?? '';
        setFormData(prev => ({ ...prev, [field]: value }));
        setIsDirty(true);
    }, []);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        const result = await onUpdate(formData);
        if (result.success) {
            setIsDirty(false);
        } else {
            setError(result.message);
        }
    }, [formData, onUpdate]);

    return (
        <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Datos de Facturación</h2>
            <p className="text-sm text-gray-500 mb-4">
                Estos datos se usan para generar facturas. Si no los completás, se usarán tus datos personales.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label htmlFor="billing-first-name" className="block text-sm font-medium text-gray-700 mb-1">
                            Nombre
                        </label>
                        <input
                            type="text"
                            id="billing-first-name"
                            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:outline-none focus:border-green-500 disabled:bg-gray-50"
                            value={formData.billing_first_name}
                            onChange={handleChange('billing_first_name')}
                            placeholder={user.name || 'Juan'}
                            autoComplete="given-name"
                            disabled={isLoading}
                        />
                    </div>
                    <div>
                        <label htmlFor="billing-last-name" className="block text-sm font-medium text-gray-700 mb-1">
                            Apellido
                        </label>
                        <input
                            type="text"
                            id="billing-last-name"
                            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:outline-none focus:border-green-500 disabled:bg-gray-50"
                            value={formData.billing_last_name}
                            onChange={handleChange('billing_last_name')}
                            placeholder={user.lastname || 'Pérez'}
                            autoComplete="family-name"
                            disabled={isLoading}
                        />
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label htmlFor="billing-dni" className="block text-sm font-medium text-gray-700 mb-1">
                            DNI / CUIT
                        </label>
                        <input
                            type="text"
                            id="billing-dni"
                            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:outline-none focus:border-green-500 disabled:bg-gray-50"
                            value={formData.billing_dni}
                            onChange={handleChange('billing_dni')}
                            placeholder="12345678"
                            autoComplete="off"
                            disabled={isLoading}
                        />
                    </div>
                    <div>
                        <label htmlFor="billing-birth-date" className="block text-sm font-medium text-gray-700 mb-1">
                            Fecha de nacimiento
                        </label>
                        <input
                            type="date"
                            id="billing-birth-date"
                            className="w-full rounded-lg border border-gray-300 px-3 py-2.5 focus:outline-none focus:border-green-500 disabled:bg-gray-50"
                            value={formData.billing_birth_date}
                            onChange={handleChange('billing_birth_date')}
                            disabled={isLoading}
                        />
                    </div>
                </div>

                {error && (
                    <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 text-sm text-red-700">
                        {error}
                    </div>
                )}

                <Button type="submit" disabled={isLoading || !isDirty}>
                    {isLoading ? 'Guardando...' : 'Guardar cambios'}
                </Button>
            </form>
        </div>
    );
}
